import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { LockIcon, UserIcon, ActivityIcon, MessageSquareIcon, CalendarIcon, FileTextIcon, BellIcon, LogOutIcon } from 'lucide-react';
const testResults = [{
  id: 1,
  date: 'Feb 20, 2026',
  name: 'Comprehensive Metabolic Panel',
  status: 'Normal'
}, {
  id: 2,
  date: 'Feb 20, 2026',
  name: 'Lipid Panel',
  status: 'Review Needed'
}, {
  id: 3,
  date: 'Jan 15, 2026',
  name: 'Complete Blood Count (CBC)',
  status: 'Normal'
}];
const messages = [{
  id: 1,
  provider: 'Dr. Sarah Mitchell',
  subject: 'Follow up on your recent lab results',
  date: 'Feb 22, 2026',
  unread: true
}, {
  id: 2,
  provider: 'Clinical Team',
  subject: 'Appointment Reminder',
  date: 'Feb 10, 2026',
  unread: false
}];
const appointments = [{
  id: 1,
  date: 'Mar 15, 2026 at 10:00 AM',
  provider: 'Dr. Sarah Mitchell',
  type: 'Follow-up Visit',
  status: 'Upcoming'
}, {
  id: 2,
  date: 'Feb 18, 2026 at 2:30 PM',
  provider: 'Dr. Sarah Mitchell',
  type: 'Annual Physical',
  status: 'Completed'
}];
export function PatientPortalPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setIsLoggedIn(true);
    }, 1000);
  };
  if (!isLoggedIn) {
    return <>
        <PageHero eyebrow="Secure Access" title="Patient Portal" subtitle="Access your medical records, message your care team, and manage appointments." data-id="element-531" />
        <section className="py-20 bg-gray-50 min-h-[60vh] flex items-center justify-center px-4" data-id="element-532">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white p-8 md:p-10 rounded-[10px] shadow-lg max-w-md w-full border border-[#E5E9F0]" data-id="element-533">
            <div className="text-center mb-8" data-id="element-534">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#0A71B1]/10 text-[#0A71B1] mb-4" data-id="element-535">
                <LockIcon className="h-8 w-8" data-id="element-536" />
              </div>
              <h2 className="text-2xl font-bold text-navy-500" data-id="element-537">Sign In</h2>
              <p className="text-gray-500 mt-2" data-id="element-538">
                Enter your credentials to access your portal
              </p>
            </div>
            <form onSubmit={handleLogin} className="space-y-5" data-id="element-539">
              <div data-id="element-540">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-541">
                  Email Address
                </label>
                <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="you@example.com" data-id="element-542" />
              </div>
              <div data-id="element-543">
                <div className="flex justify-between items-center mb-2" data-id="element-544">
                  <label className="block text-sm font-semibold text-gray-700" data-id="element-545">
                    Password
                  </label>
                  <a href="#" className="text-xs text-[#0A71B1] hover:underline font-semibold" data-id="element-546">
                    Forgot Password?
                  </a>
                </div>
                <input required type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="••••••••" data-id="element-547" />
              </div>
              <button type="submit" disabled={isLoading} className="w-full bg-[#E8891D] hover:bg-[#d07a15] text-white py-3.5 rounded-full font-bold transition-colors mt-4 disabled:opacity-70" data-id="element-548">
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
            <div className="mt-8 text-center text-sm text-gray-500" data-id="element-549">
              Don't have an account?{' '}
              <a href="#" className="text-[#0A71B1] font-semibold hover:underline" data-id="element-550">
                Register here
              </a>
            </div>
          </motion.div>
        </section>
      </>;
  }
  return <div className="bg-gray-50 min-h-screen pb-20" data-id="element-551">
      {/* Dashboard Header */}
      <div className="bg-[#002D62] text-white pt-10 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-552">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-6" data-id="element-553">
          <div className="flex items-center gap-4" data-id="element-554">
            <div className="h-16 w-16 bg-white/10 rounded-full flex items-center justify-center border border-white/20" data-id="element-555">
              <UserIcon className="h-8 w-8 text-white" data-id="element-556" />
            </div>
            <div data-id="element-557">
              <h1 className="text-2xl md:text-3xl font-bold" data-id="element-558">
                Welcome back, Sarah
              </h1>
              <p className="text-white/70 mt-1" data-id="element-559">
                DOB: 04/12/1985 | ID: #98765432
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4" data-id="element-560">
            <button className="relative p-2 text-white/80 hover:text-white transition-colors" data-id="element-561">
              <BellIcon className="h-6 w-6" data-id="element-562" />
              <span className="absolute top-1 right-1 h-2.5 w-2.5 bg-[#E8891D] rounded-full border-2 border-[#002D62]" data-id="element-563"></span>
            </button>
            <button onClick={() => setIsLoggedIn(false)} className="flex items-center gap-2 text-sm font-semibold bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors border border-white/20" data-id="element-564">
              <LogOutIcon className="h-4 w-4" data-id="element-565" /> Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10" data-id="element-566">
        <div className="bg-white rounded-[10px] shadow-sm border border-[#E5E9F0] overflow-hidden" data-id="element-567">
          {/* Tabs */}
          <div className="flex overflow-x-auto border-b border-[#E5E9F0] bg-gray-50/50" data-id="element-568">
            {[{
            id: 'overview',
            label: 'Overview',
            icon: <ActivityIcon className="h-4 w-4" data-id="element-569" />
          }, {
            id: 'results',
            label: 'Test Results',
            icon: <FileTextIcon className="h-4 w-4" data-id="element-570" />
          }, {
            id: 'messages',
            label: 'Messages',
            icon: <MessageSquareIcon className="h-4 w-4" data-id="element-571" />
          }, {
            id: 'appointments',
            label: 'Appointments',
            icon: <CalendarIcon className="h-4 w-4" data-id="element-572" />
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#0A71B1] text-[#0A71B1] bg-white' : 'border-transparent text-gray-500 hover:text-navy-500 hover:bg-gray-100'}`} data-id="element-573">
                {tab.icon} {tab.label}
              </button>)}
          </div>

          {/* Tab Content */}
          <div className="p-6 md:p-8" data-id="element-574">
            {activeTab === 'overview' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="space-y-8" data-id="element-575">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-576">
                  <div className="md:col-span-2 bg-blue-50 border border-blue-100 rounded-[10px] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-577">
                    <div data-id="element-578">
                      <span className="inline-block px-2.5 py-1 bg-blue-100 text-[#0A71B1] text-xs font-bold uppercase tracking-wider rounded-full mb-2" data-id="element-579">
                        Upcoming Appointment
                      </span>
                      <h3 className="text-lg font-bold text-navy-500" data-id="element-580">
                        Follow-up Visit with Dr. Mitchell
                      </h3>
                      <p className="text-gray-600 mt-1 flex items-center gap-2" data-id="element-581">
                        <CalendarIcon className="h-4 w-4" data-id="element-582" /> March 15, 2026 at
                        10:00 AM
                      </p>
                    </div>
                    <button className="bg-[#0A71B1] hover:bg-[#085d91] text-white px-5 py-2 rounded-[10px] font-semibold text-sm transition-colors whitespace-nowrap" data-id="element-583">
                      Reschedule
                    </button>
                  </div>
                  <div className="bg-orange-50 border border-orange-100 rounded-[10px] p-6" data-id="element-584">
                    <h3 className="text-lg font-bold text-navy-500 mb-2" data-id="element-585">
                      Action Required
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-586">
                      You have 1 new message and 1 test result requiring review.
                    </p>
                    <button onClick={() => setActiveTab('messages')} className="text-[#E8891D] font-bold text-sm hover:underline flex items-center gap-1" data-id="element-587">
                      View Messages <ChevronRightIcon className="h-4 w-4" data-id="element-588" />
                    </button>
                  </div>
                </div>

                <div data-id="element-589">
                  <h3 className="text-xl font-bold text-navy-500 mb-4" data-id="element-590">
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-591">
                    {[{
                  label: 'Request Refill',
                  icon: <ActivityIcon data-id="element-592" />
                }, {
                  label: 'Pay Bill',
                  icon: <FileTextIcon data-id="element-593" />
                }, {
                  label: 'Update Insurance',
                  icon: <UserIcon data-id="element-594" />
                }, {
                  label: 'Download Records',
                  icon: <LockIcon data-id="element-595" />
                }].map(action => <button key={action.label} className="flex flex-col items-center justify-center p-4 border border-[#E5E9F0] rounded-[10px] hover:bg-gray-50 hover:border-[#0A71B1]/30 transition-all text-navy-500 gap-3" data-id="element-596">
                        <div className="text-[#0A71B1]" data-id="element-597">{action.icon}</div>
                        <span className="text-sm font-semibold text-center" data-id="element-598">
                          {action.label}
                        </span>
                      </button>)}
                  </div>
                </div>
              </motion.div>}

            {activeTab === 'results' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-599">
                <h3 className="text-xl font-bold text-navy-500 mb-6" data-id="element-600">
                  Recent Test Results
                </h3>
                <div className="overflow-x-auto" data-id="element-601">
                  <table className="w-full text-left border-collapse" data-id="element-602">
                    <thead data-id="element-603">
                      <tr className="border-b border-[#E5E9F0] text-sm text-gray-500 uppercase tracking-wider" data-id="element-604">
                        <th className="pb-3 font-semibold" data-id="element-605">Date</th>
                        <th className="pb-3 font-semibold" data-id="element-606">Test Name</th>
                        <th className="pb-3 font-semibold" data-id="element-607">Status</th>
                        <th className="pb-3 font-semibold text-right" data-id="element-608">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E9F0]" data-id="element-609">
                      {testResults.map(result => <tr key={result.id} className="hover:bg-gray-50 transition-colors" data-id="element-610">
                          <td className="py-4 text-sm text-gray-600" data-id="element-611">
                            {result.date}
                          </td>
                          <td className="py-4 font-medium text-navy-500" data-id="element-612">
                            {result.name}
                          </td>
                          <td className="py-4" data-id="element-613">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${result.status === 'Normal' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`} data-id="element-614">
                              {result.status}
                            </span>
                          </td>
                          <td className="py-4 text-right" data-id="element-615">
                            <button className="text-[#0A71B1] hover:underline text-sm font-semibold" data-id="element-616">
                              View Report
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </motion.div>}

            {activeTab === 'messages' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-617">
                <div className="flex justify-between items-center mb-6" data-id="element-618">
                  <h3 className="text-xl font-bold text-navy-500" data-id="element-619">
                    Secure Messages
                  </h3>
                  <button className="bg-[#0A71B1] hover:bg-[#085d91] text-white px-4 py-2 rounded-[10px] text-sm font-semibold transition-colors" data-id="element-620">
                    New Message
                  </button>
                </div>
                <div className="space-y-3" data-id="element-621">
                  {messages.map(msg => <div key={msg.id} className={`p-4 border rounded-[10px] flex items-start gap-4 cursor-pointer transition-colors ${msg.unread ? 'bg-blue-50 border-blue-100' : 'bg-white border-[#E5E9F0] hover:bg-gray-50'}`} data-id="element-622">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${msg.unread ? 'bg-[#0A71B1] text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-623">
                        <UserIcon className="h-5 w-5" data-id="element-624" />
                      </div>
                      <div className="flex-grow" data-id="element-625">
                        <div className="flex justify-between items-start mb-1" data-id="element-626">
                          <h4 className={`text-sm ${msg.unread ? 'font-bold text-navy-500' : 'font-semibold text-gray-700'}`} data-id="element-627">
                            {msg.provider}
                          </h4>
                          <span className="text-xs text-gray-500" data-id="element-628">
                            {msg.date}
                          </span>
                        </div>
                        <p className={`text-sm ${msg.unread ? 'font-semibold text-gray-900' : 'text-gray-600'}`} data-id="element-629">
                          {msg.subject}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>}

            {activeTab === 'appointments' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-630">
                <div className="flex justify-between items-center mb-6" data-id="element-631">
                  <h3 className="text-xl font-bold text-navy-500" data-id="element-632">
                    Appointments
                  </h3>
                  <a href="/book" className="bg-[#E8891D] hover:bg-[#d07a15] text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors" data-id="element-633">
                    Book New
                  </a>
                </div>
                <div className="space-y-4" data-id="element-634">
                  {appointments.map(apt => <div key={apt.id} className="p-5 border border-[#E5E9F0] rounded-[10px] flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-sm transition-shadow" data-id="element-635">
                      <div className="flex items-start gap-4" data-id="element-636">
                        <div className="h-12 w-12 bg-gray-50 rounded-[10px] border border-gray-200 flex flex-col items-center justify-center flex-shrink-0 text-[#0A71B1]" data-id="element-637">
                          <CalendarIcon className="h-5 w-5" data-id="element-638" />
                        </div>
                        <div data-id="element-639">
                          <h4 className="font-bold text-navy-500" data-id="element-640">
                            {apt.type}
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-641">
                            {apt.provider}
                          </p>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-642">
                            {apt.date}
                          </p>
                        </div>
                      </div>
                      <div data-id="element-643">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${apt.status === 'Upcoming' ? 'bg-blue-100 text-[#0A71B1]' : 'bg-gray-100 text-gray-600'}`} data-id="element-644">
                          {apt.status}
                        </span>
                      </div>
                    </div>)}
                </div>
              </motion.div>}
          </div>
        </div>
      </div>
    </div>;
}